/**
 * Подвкладка «Трекинг» вкладки «Доставка» (владелец 23.10.26): треки посылок сделки из CRM. Карточка
 * трека читается сверху вниз: направление (чип по виду: клиенту / от клиента / между салонами) и
 * маршрут-полоса «ЕКБ ●──► ЧЛБ» крупно, под концами — город, адрес или ПВЗ и человек; номер для людей
 * (`display`, у Яндекса — клиентский, сервисный `…-udp` мелко) с копированием и ссылками «следить» /
 * «кабинет»; статус CRM (цвет из CRM) и статус СДЭК; кнопки «Накладная PDF» (скачать) и печать;
 * товары трека фото-карточками; хронология «создан → отправлен → доставлен» одной строкой мелко.
 * «Привязать товары» — окно с карточками вещей договора (пишет в боевую CRM через legacy, право
 * `delivery_edit`); «Найти по номеру» — номер, который прислал клиент, среди уже заведённых треков;
 * «В чат» — номер и PDF накладной в поле ответа (не отправляет). Трек заводит только Резерв.
 * `TrackLine` живёт и в карточке серии (без товаров), `ProductTrackPills` — у товаров «Договора»,
 * `TrackBubble` — в ленте чата: «создан трек» по дате создания, клик открывает этот трек здесь
 * (`?panel=delivery&dsub=track&track=<id>` — карточка прокручивается и подсвечивается).
 * Спека `docs/specs/2026-10-23-deal-tracks-design.md`.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeftRight,
  ArrowRight,
  Building2,
  Check,
  CircleHelp,
  Copy,
  Download,
  ExternalLink,
  Link2,
  type LucideIcon,
  MapPin,
  MessageSquarePlus,
  Package,
  Printer,
  Route,
  Search,
  Send,
  Store,
  Undo2,
  UserRound,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router";

import type { OnOffer } from "@/components/chat/SerialPhotos";
import {
  type Track,
  type TrackDirection,
  type TrackDirectionKind,
  type TrackProduct,
  type TrackSide,
  bindTrack,
  dealTracksKey,
  dt,
  findTracks,
  loadDealTracks,
  trackForChat,
  waybillUrl,
} from "@/lib/dealTracks";

const card = "rounded-[var(--radius-card)] border border-border bg-bg-subtle p-3 text-[12.5px]";
const btn =
  "inline-flex h-7 items-center gap-1 rounded-[var(--radius-ui)] border border-border px-2 text-[12px] text-fg hover:bg-bg-hover disabled:opacity-50";
const btnIcon =
  "inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-[var(--radius-ui)] border border-border text-fg-muted hover:bg-bg-hover hover:text-fg";
const btnTall =
  "inline-flex h-8 shrink-0 items-center gap-1 rounded-[var(--radius-ui)] border border-border px-2 text-[12px] text-fg hover:bg-bg-hover disabled:opacity-50";
const btnMain =
  "inline-flex h-8 items-center gap-1 rounded-[var(--radius-ui)] bg-accent px-3 text-[12.5px] font-medium text-white hover:opacity-90 disabled:opacity-50";
const input =
  "h-8 w-full rounded-[var(--radius-ui)] border border-border bg-bg px-2 text-[12.5px] text-fg placeholder:text-fg-faint";

/** «23.09» из «2026-09-23 00:00». */
const day = (s: string | null) => (s ? `${s.slice(8, 10)}.${s.slice(5, 7)}` : "");

/** Вид направления: цвет чипа и полосы маршрута, иконка. Клиенту — акцент, от клиента — предупреждение
 *  (посылку надо получить), между салонами и прочее — нейтрально. */
const DIR: Record<TrackDirectionKind, { chip: string; line: string; icon: LucideIcon }> = {
  to_client: { chip: "bg-accent-dim text-accent-text", line: "text-accent", icon: Send },
  from_client: { chip: "bg-warn-bg text-warn", line: "text-warn", icon: Undo2 },
  between: { chip: "bg-neutral-bg text-fg", line: "text-fg-muted", icon: ArrowLeftRight },
  from_salon: { chip: "bg-neutral-bg text-fg-muted", line: "text-fg-muted", icon: ArrowRight },
  to_salon: { chip: "bg-neutral-bg text-fg-muted", line: "text-fg-muted", icon: ArrowRight },
  other: { chip: "bg-neutral-bg text-fg-muted", line: "text-fg-muted", icon: Route },
};

const SIDE_ICON: Record<TrackSide["kind"], LucideIcon> = { salon: Store, client: UserRound, address: MapPin, unknown: CircleHelp };

/** Крупная подпись конца маршрута: код салона, «Клиент», адрес или «?». */
function sideLabel(s: TrackSide): string {
  if (s.kind === "salon") return s.name || s.title || "Салон";
  if (s.kind === "client") return "Клиент";
  if (s.kind === "address") return s.address || "Адрес";
  return "?";
}

/** Мелкие строки под концом: полное имя салона (если отличается от кода), адрес / ПВЗ, человек. */
function sideDetails(s: TrackSide): string[] {
  const out: string[] = [];
  if (s.kind === "salon" && s.title && s.title !== s.name) out.push(s.title);
  if (s.kind !== "address" && s.address) out.push(s.address);
  if (s.person) out.push(s.person);
  return out;
}

/** Короткий маршрут для пилюль и подсказок: «ЕКБ → ЧЛБ», «→ клиенту», «от клиента → СМР». */
function shortRoute(d: TrackDirection): string {
  const from = d.from.kind === "client" ? "от клиента" : d.from.kind === "unknown" ? "" : sideLabel(d.from);
  const to = d.to.kind === "client" ? "клиенту" : d.to.kind === "unknown" ? "" : sideLabel(d.to);
  if (!from && !to) return d.label;
  return `${from}${from ? " " : ""}→${to ? ` ${to}` : ""}`;
}

/** Копировать в буфер: галочка на полторы секунды. */
function CopyButton({ text, label = "Скопировать номер", size = 12, className }: { text: string; label?: string; size?: number; className?: string }) {
  const [done, setDone] = useState(false);
  useEffect(() => {
    if (!done) return;
    const id = window.setTimeout(() => setDone(false), 1500);
    return () => window.clearTimeout(id);
  }, [done]);
  return (
    <button
      type="button"
      className={className ?? "inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-fg-muted hover:bg-bg-hover hover:text-fg"}
      title={done ? "Скопировано" : label}
      aria-label={label}
      onClick={() => {
        void navigator.clipboard?.writeText(text).then(
          () => setDone(true),
          () => undefined,
        );
      }}
    >
      {done ? <Check size={size} className="text-ok" /> : <Copy size={size} />}
    </button>
  );
}

function Thumb({ p, size = "h-12 w-9" }: { p: TrackProduct; size?: string }) {
  return p.photo ? (
    <img src={p.photo} alt="" className={`${size} shrink-0 rounded object-cover`} loading="lazy" />
  ) : (
    <div className={`${size} grid shrink-0 place-items-center rounded bg-bg-hover text-fg-faint`}>
      <Package size={14} />
    </div>
  );
}

/** Полоса маршрута: «ЕКБ ●──► ЧЛБ» крупно, под концами — город, адрес, человек. */
function RouteStrip({ d }: { d: TrackDirection }) {
  const style = DIR[d.kind] ?? DIR.other;
  const FromIcon = SIDE_ICON[d.from.kind] ?? CircleHelp;
  const ToIcon = SIDE_ICON[d.to.kind] ?? CircleHelp;
  const fromMore = sideDetails(d.from);
  const toMore = sideDetails(d.to);
  const fromLabel = sideLabel(d.from);
  const toLabel = sideLabel(d.to);
  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-center gap-2">
        <span className="flex min-w-0 max-w-[45%] items-center gap-1.5 text-[15px] font-semibold leading-tight text-fg" title={fromLabel}>
          <FromIcon size={15} className={`shrink-0 ${style.line}`} />
          <span className="truncate">{fromLabel}</span>
        </span>
        <span className={`flex min-w-[36px] flex-1 items-center ${style.line}`} aria-label="→">
          <span className="h-2 w-2 shrink-0 rounded-full bg-current" />
          <span className="h-px flex-1 bg-current" />
          <ArrowRight size={16} className="-ml-1.5 shrink-0" />
        </span>
        <span className="flex min-w-0 max-w-[45%] items-center gap-1.5 text-[15px] font-semibold leading-tight text-fg" title={toLabel}>
          <ToIcon size={15} className={`shrink-0 ${style.line}`} />
          <span className="truncate">{toLabel}</span>
        </span>
      </div>
      {(fromMore.length > 0 || toMore.length > 0) && (
        <div className="grid grid-cols-2 gap-x-3 text-[11.5px] leading-snug text-fg-muted">
          <div className="min-w-0 break-words">
            {fromMore.map((s, i) => (
              <div key={i} className={i === 0 ? "" : "text-fg-faint"}>
                {s}
              </div>
            ))}
          </div>
          <div className="min-w-0 break-words text-right">
            {toMore.map((s, i) => (
              <div key={i} className={i === 0 ? "" : "text-fg-faint"}>
                {s}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/** Кнопки накладной: скачать PDF и открыть для печати во вкладке. Тот же домен — cookie сессии. */
function WaybillButtons({ t }: { t: Track }) {
  if (!t.waybill_pdf) return null;
  const label = t.waybill_name?.startsWith("Наклейка") ? "Наклейка PDF" : "Накладная PDF";
  return (
    <>
      <a href={waybillUrl(t.id)} download={t.waybill_name ?? undefined} className={`${btn} font-medium`} title={t.waybill_name ?? label}>
        <Download size={13} />
        {label}
      </a>
      <a href={waybillUrl(t.id, true)} target="_blank" rel="noreferrer" className={btnIcon} title="Открыть для печати" aria-label="Открыть накладную для печати">
        <Printer size={13} />
      </a>
    </>
  );
}

/** Трек целиком: направление и маршрут, номер, статус, накладная, вещи, хронология. */
export function TrackLine({ t, extra }: { t: Track; extra?: React.ReactNode }) {
  const d = t.direction;
  const style = DIR[d.kind] ?? DIR.other;
  const DirIcon = style.icon;
  const trackUrl = t.url && t.url !== t.account_url ? t.url : null;
  const accountUrl = t.account_url ?? (trackUrl ? null : t.url);
  const chrono = [
    t.created ? `создан ${dt(t.created)}${t.user ? `, ${t.user}` : ""}` : t.user,
    t.sent ? `отправлен ${dt(t.sent)}` : "",
    t.delivered ? `доставлен ${dt(t.delivered)}` : "",
  ].filter(Boolean);
  const showDue = t.due && !t.delivered && !t.cancelled;
  return (
    <div className={`flex flex-col gap-2 ${t.cancelled ? "opacity-60" : ""}`}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-px text-[11.5px] font-medium ${style.chip}`} title={t.category}>
          <DirIcon size={12} />
          {d.label}
        </span>
        <span className="font-medium text-fg">{t.company || "Служба?"}</span>
        {t.group && t.group !== "Общее" && <span className="rounded-full bg-neutral-bg px-2 py-px text-[11px] text-fg-muted">{t.group}</span>}
        <span
          className="ml-auto rounded-full px-2 py-px text-[11.5px] font-semibold"
          style={{ color: t.status_color ?? undefined, background: t.status_color ? `${t.status_color}1f` : undefined }}
        >
          {t.status || "статус?"}
        </span>
      </div>

      <RouteStrip d={d} />

      <div className="flex flex-col gap-0.5">
        <div className="flex flex-wrap items-center gap-x-1 gap-y-0.5">
          {t.display ? (
            <>
              <span className="select-all font-mono text-[16px] font-semibold tracking-tight text-fg">{t.display}</span>
              <CopyButton text={t.display} />
            </>
          ) : (
            <span className="text-fg-faint">без номера</span>
          )}
          {trackUrl && (
            <a href={trackUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 text-[12px] text-accent-text hover:underline" title="Отследить на сайте службы">
              следить
              <ExternalLink size={11} />
            </a>
          )}
          {accountUrl && (
            <a
              href={accountUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-0.5 text-[12px] text-accent-text hover:underline"
              title="Трек в личном кабинете службы (сотруднику)"
            >
              <Building2 size={11} />
              кабинет
            </a>
          )}
        </div>
        {t.service && (
          <div className="flex min-w-0 items-center gap-1 text-[11px] text-fg-faint">
            <span className="shrink-0">сервисный</span>
            <span className="min-w-0 truncate font-mono" title={t.service}>
              {t.service}
            </span>
            <CopyButton text={t.service} label="Скопировать сервисный номер" size={11} className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-fg-faint hover:bg-bg-hover hover:text-fg" />
          </div>
        )}
      </div>

      {(t.cdek_status || showDue) && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[12px] text-fg-muted">
          {t.cdek_status && (
            <span title="Статус СДЭК">
              СДЭК: <span className="font-medium text-fg">{t.cdek_status}</span>
              {t.cdek_status_at && <span className="text-fg-faint"> · {dt(t.cdek_status_at)}</span>}
            </span>
          )}
          {showDue && <span>доставят до {day(t.due)}</span>}
        </div>
      )}

      {(t.waybill_pdf || extra) && (
        <div className="flex flex-wrap items-center gap-1">
          <WaybillButtons t={t} />
          {extra}
        </div>
      )}

      {t.products.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {t.products.map((p) => (
            <div key={p.serial} className="flex items-center gap-1.5 rounded-[var(--radius-ui)] border border-border bg-bg py-1 pl-1 pr-2" title={p.name}>
              <Thumb p={p} size="h-10 w-[30px]" />
              <span className="max-w-[160px] truncate text-[11.5px]">{p.name}</span>
            </div>
          ))}
        </div>
      )}

      {(chrono.length > 0 || t.comment || t.category) && (
        <div className="text-[11px] leading-snug text-fg-faint">
          {chrono.length > 0 && <div>{chrono.join(" → ")}</div>}
          {(t.category || t.comment) && (
            <div>
              {t.category}
              {t.category && t.comment && " · "}
              {t.comment && `«${t.comment}»`}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function BindDialog({ dealId, t, products, onClose }: { dealId: number; t: Track; products: TrackProduct[]; onClose: () => void }) {
  const qc = useQueryClient();
  const bound = new Set(t.products.map((p) => p.serial));
  // Ни одной вещи договора на треке — отмечаем все; иначе — то, чего на треке ещё нет.
  const none = !products.some((p) => bound.has(p.serial));
  const [picked, setPicked] = useState<Set<number>>(() => new Set(products.filter((p) => none || !bound.has(p.serial)).map((p) => p.serial)));
  const save = useMutation({
    mutationFn: () => bindTrack(dealId, t.id, [...picked]),
    onSuccess: (d) => {
      qc.setQueryData(dealTracksKey(dealId), d);
      for (const s of picked) void qc.invalidateQueries({ queryKey: ["serial-tracks", s] });
      onClose();
    },
  });
  const toggle = (s: number) =>
    setPicked((x) => {
      const n = new Set(x);
      if (n.has(s)) n.delete(s);
      else n.add(s);
      return n;
    });
  return createPortal(
    <div className="fixed inset-0 z-[90] grid place-items-center bg-black/30 p-4" onClick={onClose} onKeyDown={(e) => e.key === "Escape" && onClose()}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Привязать товары к трекингу"
        className="flex max-h-[90vh] w-full max-w-2xl flex-col gap-3 overflow-auto rounded-[var(--radius-ui)] border border-border bg-bg p-4 text-[12.5px] text-fg shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <div className="mb-2 text-[14px] font-semibold">Привязать товары к трекингу</div>
            <div className={card}>
              <TrackLine t={t} />
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-fg-muted hover:text-fg" aria-label="Закрыть">
            <X size={16} />
          </button>
        </div>
        {products.length === 0 ? (
          <div className="text-fg-muted">В договоре сделки нет вещей — трек привяжется только к сделке.</div>
        ) : (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(140px,1fr))] gap-2">
            {products.map((p) => {
              const on = picked.has(p.serial);
              return (
                <button
                  key={p.serial}
                  type="button"
                  onClick={() => toggle(p.serial)}
                  aria-pressed={on}
                  className={`relative flex flex-col gap-1 rounded-[var(--radius-ui)] border p-1.5 text-left ${on ? "border-accent ring-1 ring-accent" : "border-border hover:bg-bg-hover"}`}
                >
                  {p.photo ? (
                    <img src={p.photo} alt="" className="aspect-[3/4] w-full rounded object-cover" loading="lazy" />
                  ) : (
                    <div className="grid aspect-[3/4] w-full place-items-center rounded bg-bg-hover text-fg-faint">
                      <Package size={22} />
                    </div>
                  )}
                  <span className="line-clamp-2 text-[11.5px]">{p.name}</span>
                  {bound.has(p.serial) && <span className="text-[11px] text-fg-muted">уже на треке</span>}
                  {on && (
                    <span className="absolute right-2 top-2 grid h-5 w-5 place-items-center rounded-full bg-accent text-white">
                      <Check size={13} />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
        {save.isError && <div className="text-[12px] text-err">{save.error.message}</div>}
        <div className="flex items-center gap-2">
          <button type="button" className={btnMain} disabled={save.isPending} onClick={() => save.mutate()}>
            <Link2 size={14} />
            {save.isPending ? "Привязываю…" : picked.size ? `Привязать: сделка + ${picked.size} шт.` : "Привязать к сделке"}
          </button>
          <span className="text-[11.5px] text-fg-faint">Пишет в CRM: трек появится в карточке товара и сделки.</span>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** Номер, который прислал клиент: ищем среди треков, заведённых в CRM. */
function FindBox({ onPick }: { onPick: (t: Track) => void }) {
  const [q, setQ] = useState("");
  const find = useMutation({ mutationFn: () => findTracks(q) });
  return (
    <div className="flex flex-col gap-2 rounded-[var(--radius-card)] border border-border bg-bg p-2">
      <form
        className="flex gap-1"
        onSubmit={(e) => {
          e.preventDefault();
          if (q.trim()) find.mutate();
        }}
      >
        <input className={input} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Номер трека или накладной" autoFocus />
        <button type="submit" className={btnTall} disabled={find.isPending || !q.trim()}>
          <Search size={13} />
          Найти
        </button>
      </form>
      {find.isError && <div className="text-[12px] text-err">{find.error.message}</div>}
      {find.data?.length === 0 && <div className="text-[12px] text-fg-muted">В CRM такого трека нет — его заводят в Резерве.</div>}
      {find.data?.map((t) => (
        <div key={t.id} className={card}>
          <TrackLine
            t={t}
            extra={
              <button type="button" className={`${btn} border-accent text-accent-text`} onClick={() => onPick(t)}>
                <Link2 size={12} />
                Привязать к сделке
              </button>
            }
          />
        </div>
      ))}
    </div>
  );
}

function ToChat({ t, onOffer }: { t: Track; onOffer: OnOffer }) {
  const send = useMutation({
    mutationFn: () => trackForChat(t.id),
    onSuccess: (d) => onOffer(d.message, d.files),
  });
  return (
    <button
      type="button"
      className={btn}
      disabled={send.isPending}
      onClick={() => send.mutate()}
      title={send.isError ? send.error.message : t.waybill_pdf ? "Номер и накладная PDF — в поле ответа" : "Номер — в поле ответа"}
    >
      <MessageSquarePlus size={12} />
      {send.isPending ? "Готовлю…" : send.isError ? "Не вышло" : "В чат"}
    </button>
  );
}

/** Пилюли «Договора»: по каким трекам сделки едет вещь — куда, номер, статус (отменённые не показываем). */
export function ProductTrackPills({ dealId, serial }: { dealId: number; serial: number }) {
  const q = useQuery({ queryKey: dealTracksKey(dealId), queryFn: () => loadDealTracks(dealId), staleTime: 60_000 });
  const tracks = (q.data?.tracks ?? []).filter((t) => !t.cancelled && t.products.some((p) => p.serial === serial));
  return (
    <>
      {tracks.map((t) => {
        const style = DIR[t.direction.kind] ?? DIR.other;
        const cls = "inline-flex max-w-full items-center gap-1 rounded-full border border-border px-2 py-px text-[11px] text-fg";
        const title = [t.company, t.direction.label, t.display, t.status, t.cdek_status && `СДЭК: ${t.cdek_status}`].filter(Boolean).join(" · ");
        const body = (
          <>
            <Package size={11} className={`shrink-0 ${style.line}`} />
            <span className="truncate font-medium">{shortRoute(t.direction)}</span>
            {t.display && <span className="shrink-0 font-mono">{t.display}</span>}
            <span className="shrink-0" style={{ color: t.status_color ?? undefined }}>
              {t.status}
            </span>
          </>
        );
        const href = t.account_url ?? t.url;
        return href ? (
          <a key={t.id} href={href} target="_blank" rel="noreferrer" className={`${cls} hover:bg-bg-hover`} title={title}>
            {body}
          </a>
        ) : (
          <span key={t.id} className={cls} title={title}>
            {body}
          </span>
        );
      })}
    </>
  );
}

/** Создание трека в ленте чата — плашкой по центру, как уведомление: клиент её не видел. Клик —
 *  этот трек во вкладке «Доставка» → «Трекинг». */
export function TrackBubble({ t, onOpen }: { t: Track; onOpen: () => void }) {
  const st = DIR[t.direction.kind];
  const Icon = st.icon;
  return (
    <button
      type="button"
      onClick={onOpen}
      className="mx-auto my-1 block w-full max-w-[460px] rounded-[10px] border border-dashed border-border bg-bg-subtle px-3 py-2 text-left text-[12.5px] hover:bg-bg-hover"
      title="Открыть трек во вкладке «Доставка» → «Трекинг»"
      data-annot={`трек ${t.id} в ленте`}
    >
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11.5px] text-fg-muted">
        <Package size={13} aria-hidden />
        <span className="font-medium text-fg">Создан трек {t.company}</span>
        {t.user && <span>· {t.user}</span>}
        <span className="ml-auto tabular-nums">{t.created?.slice(11, 16)}</span>
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11.5px] font-medium ${st.chip}`}>
          <Icon size={12} aria-hidden />
          {t.direction.label}
        </span>
        <span className="font-semibold">{shortRoute(t.direction)}</span>
        {t.display && <span className="font-mono text-[12px]">{t.display}</span>}
        <span
          className="rounded px-1.5 py-px text-[11px] font-medium"
          style={{ color: t.status_color ?? undefined, background: t.status_color ? `${t.status_color}1f` : undefined }}
        >
          {t.status}
        </span>
      </div>
      {t.products.length > 0 && <div className="mt-1 truncate text-[11.5px] text-fg-muted">{t.products.map((p) => p.name).join(", ")}</div>}
    </button>
  );
}

export function DealTracks({ dealId, onOffer }: { dealId: number; onOffer?: OnOffer | undefined }) {
  const q = useQuery({ queryKey: dealTracksKey(dealId), queryFn: () => loadDealTracks(dealId) });
  const [bind, setBind] = useState<Track | null>(null);
  const [finding, setFinding] = useState(false);
  // `?track=<id>` — пришли из ленты чата: прокрутить к треку и подсветить.
  const [params] = useSearchParams();
  const focus = Number(params.get("track")) || null;
  const focused = useRef<HTMLDivElement>(null);
  useEffect(() => {
    focused.current?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [focus, q.data]);
  if (q.isPending) return <div className={`${card} text-fg-muted`}>Трекинг: загружаю…</div>;
  if (q.isError) return <div className={`${card} text-fg-muted`}>Треки не загрузились: {q.error.message}</div>;
  const d = q.data;
  const ours = new Set(d.products.map((p) => p.serial));
  const n = d.tracks.length;
  return (
    <div className="flex flex-col gap-2 text-[12.5px]">
      <div className="flex items-center gap-2">
        <span className="text-fg-muted">{n ? `${n} ${n === 1 ? "трек" : n < 5 ? "трека" : "треков"}` : "Треков нет"}</span>
        {d.can_edit && (
          <button type="button" className={`${btn} ml-auto`} onClick={() => setFinding((x) => !x)} aria-expanded={finding}>
            <Search size={12} />
            Найти по номеру
          </button>
        )}
      </div>
      {finding && (
        <FindBox
          onPick={(t) => {
            setBind(t);
            setFinding(false);
          }}
        />
      )}
      {d.tracks.map((t) => {
        const noGoods = !t.products.some((p) => ours.has(p.serial));
        return (
          <div key={t.id} ref={t.id === focus ? focused : undefined} className={`${card} ${t.id === focus ? "ring-2 ring-accent" : ""}`}>
            <TrackLine
              t={t}
              extra={
                <>
                  {onOffer && t.display && <ToChat t={t} onOffer={onOffer} />}
                  {d.can_edit && d.products.length > 0 && (
                    <button type="button" className={`${btn} ${noGoods ? "border-accent text-accent-text" : ""}`} onClick={() => setBind(t)}>
                      <Link2 size={12} />
                      {noGoods ? "Привязать товары" : "Товары"}
                    </button>
                  )}
                </>
              }
            />
          </div>
        );
      })}
      {n === 0 && !finding && (
        <div className={`${card} text-fg-muted`}>К сделке треков не привязано. Клиент прислал номер — «Найти по номеру»; новый трек заводят в Резерве.</div>
      )}
      {bind && <BindDialog dealId={dealId} t={bind} products={d.products} onClose={() => setBind(null)} />}
    </div>
  );
}
